import { useMemo, useState } from "react";
import axios from "axios";
import { adminErrorDetail } from "../lib/adminError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT = [
  ["Business idea", "#2C4A63"],
  ["Video idea", "#9E2B20"],
  ["Post idea", "#C4571F"],
  ["Comment idea", "#6E6A66"],
  ["Strategy", "#2F6B3F"],
  ["Distribution", "#5B3E8C"],
  ["Site and admin", "#1B1917"],
  ["Email", "#1F6B6B"],
  ["Research", "#A67C00"],
];
const CATEGORIES = CAT.map((c) => c[0]);
const CAT_COLOR = Object.fromEntries(CAT);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const CatBadge = ({ category, testid }) => {
  const c = CAT_COLOR[category] || "#1B1917";
  return (
    <span className="inline-block px-2 py-0.5 text-sm rounded" data-testid={testid}
      style={{ backgroundColor: "transparent", border: `1px solid ${c}`, color: c }}>
      {category}
    </span>
  );
};

const emptyForm = () => ({ title: "", category: "Business idea", body: "" });

export default function AdminNotes({ csrf, notes, reloadNotes }) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [open, setOpen] = useState(() => new Set());
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const list = notes || [];

  const counts = useMemo(() => {
    const m = { all: list.length };
    for (const c of CATEGORIES) m[c] = 0;
    for (const n of list) if (m[n.category] !== undefined) m[n.category] += 1;
    return m;
  }, [list]);

  const q = search.trim().toLowerCase();
  const filtered = list.filter((n) => {
    if (cat !== "all" && n.category !== cat) return false;
    if (q && !((n.title || "").toLowerCase().includes(q) || (n.body || "").toLowerCase().includes(q))) return false;
    return true;
  });

  const openAdd = () => { setEditingId(null); setForm(emptyForm()); setError(""); setShowForm(true); };
  const openEdit = (n) => {
    setEditingId(n.id);
    setForm({ title: n.title || "", category: n.category || "Business idea", body: n.body || "" });
    setError(""); setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setError(""); };

  const toggle = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const expandAll = () => setOpen(new Set(filtered.map((n) => n.id)));
  const collapseAll = () => setOpen(new Set());

  const submit = async () => {
    if (!form.title.trim()) { setError("Title is required."); return; }
    if (!form.category) { setError("Category is required."); return; }
    if (!form.body.trim()) { setError("Body is required."); return; }
    setSaving(true); setError("");
    try {
      const cfg = { headers: { "X-CSRF-Token": csrf }, withCredentials: true };
      const payload = { title: form.title.trim().slice(0, 120), category: form.category, body: form.body.slice(0, 5000) };
      if (editingId) await axios.put(`${API}/admin/notes/${editingId}`, payload, cfg);
      else await axios.post(`${API}/admin/notes`, payload, cfg);
      await reloadNotes();
      closeForm();
    } catch (e) {
      setError(adminErrorDetail(e, "Saving failed. Log out and back in, then try again."));
    } finally { setSaving(false); }
  };

  const deleteNote = async (n) => {
    setDeleting(true); setActionError("");
    try {
      await axios.delete(`${API}/admin/notes/${n.id}`, { headers: { "X-CSRF-Token": csrf }, withCredentials: true });
      setConfirmDeleteId(null);
      await reloadNotes();
    } catch (e) {
      setActionError(`Could not delete "${n.title}". ${adminErrorDetail(e, "please try again")}`);
    } finally { setDeleting(false); }
  };

  return (
    <div data-testid="table-notes">
      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <h2 className="font-display text-2xl" style={{ fontWeight: 800 }}>Notes</h2>
        <button
          onClick={showForm && !editingId ? closeForm : openAdd}
          className="label-mono border border-ink text-ink px-4 py-2 hover:bg-ink hover:text-white transition-colors"
          data-testid="note-add-toggle"
        >
          {showForm && !editingId ? "Close" : "Add note"}
        </button>
      </div>

      {showForm && (
        <div className="mb-8 border border-hairline p-4" data-testid="note-form">
          <div className="label-mono text-muted mb-3">{editingId ? "Edit note" : "Add note"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="label-mono text-muted mb-1" htmlFor="note-title">Title * ({form.title.length}/120)</label>
              <input id="note-title" type="text" value={form.title} maxLength={120}
                onChange={(e) => set("title", e.target.value)}
                className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none"
                data-testid="note-title" />
            </div>
            <div className="flex flex-col">
              <label className="label-mono text-muted mb-1" htmlFor="note-category">Category *</label>
              <select id="note-category" value={form.category} onChange={(e) => set("category", e.target.value)}
                className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none"
                data-testid="note-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex flex-col md:col-span-2">
              <label className="label-mono text-muted mb-1" htmlFor="note-body">Body * ({form.body.length}/5000)</label>
              <textarea id="note-body" value={form.body} maxLength={5000} rows={6}
                onChange={(e) => set("body", e.target.value)}
                className="bg-transparent border border-hairline focus:border-ink py-1 px-2 text-sm outline-none resize-y"
                data-testid="note-body" />
            </div>
          </div>
          {error && <p className="mt-3 text-sm text-red-600" data-testid="note-form-error">{error}</p>}
          <div className="mt-4 flex items-center gap-3">
            <button onClick={submit} disabled={saving}
              className="label-mono border border-ink text-ink px-4 py-2 hover:bg-ink hover:text-white transition-colors disabled:opacity-40"
              data-testid="note-save">
              {saving ? "Saving…" : editingId ? "Save changes" : "Add note"}
            </button>
            <button onClick={closeForm} className="label-mono text-muted hover:text-ink transition-colors" data-testid="note-cancel">Cancel</button>
          </div>
        </div>
      )}

      {actionError && <p className="mb-4 text-sm text-red-600" data-testid="note-action-error">{actionError}</p>}

      {list.length === 0 ? (
        <p className="text-muted" data-testid="notes-empty">No notes yet.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="note-category-filters">
            <button onClick={() => setCat("all")}
              className={`label-mono px-3 py-1.5 border transition-colors ${cat === "all" ? "bg-ink text-white border-ink" : "border-hairline text-muted hover:text-ink"}`}
              data-testid="note-filter-all">
              All ({counts.all})
            </button>
            {CATEGORIES.map((c) => {
              const color = CAT_COLOR[c];
              const active = cat === c;
              return (
                <button key={c} onClick={() => setCat(c)}
                  className="label-mono px-3 py-1.5 border transition-colors"
                  style={active
                    ? { backgroundColor: color, borderColor: color, color: "#FFFFFF" }
                    : { backgroundColor: "transparent", borderColor: color, color }}
                  data-testid={`note-filter-${c}`}>
                  {c} ({counts[c]})
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <div className="relative w-64">
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search notes..."
                className="bg-transparent border-b border-hairline focus:border-ink py-1 pr-6 text-sm outline-none w-64"
                data-testid="notes-search" />
              {search && (
                <button onClick={() => setSearch("")} aria-label="Clear search"
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-muted hover:text-ink text-sm leading-none px-1"
                  data-testid="notes-search-clear">×</button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={expandAll} className="label-mono text-muted hover:text-ink transition-colors" data-testid="notes-expand-all">Expand all</button>
              <span className="text-hairline">·</span>
              <button onClick={collapseAll} className="label-mono text-muted hover:text-ink transition-colors" data-testid="notes-collapse-all">Collapse all</button>
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="text-muted" data-testid="notes-no-results">No notes match your filters.</p>
          ) : (
            <div className="border border-hairline divide-y divide-hairline" data-testid="notes-list">
              {filtered.map((n) => {
                const isOpen = open.has(n.id);
                return (
                  <div key={n.id} data-testid={`note-row-${n.id}`}>
                    <div className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-surface"
                      onClick={() => toggle(n.id)} data-testid={`note-header-${n.id}`}>
                      <span className="label-mono text-muted whitespace-nowrap w-28 shrink-0" data-testid={`note-date-${n.id}`}>{fmtDate(n.created_at)}</span>
                      <CatBadge category={n.category} testid={`note-cat-${n.id}`} />
                      <span className="text-ink flex-1 min-w-0 truncate" data-testid={`note-title-${n.id}`}>{n.title}</span>
                      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {confirmDeleteId === n.id ? (
                          <span className="flex items-center gap-2" data-testid={`note-delete-confirm-${n.id}`}>
                            <span className="label-mono text-muted">Delete "{n.title}"?</span>
                            <button onClick={() => deleteNote(n)} disabled={deleting}
                              className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors disabled:opacity-40"
                              data-testid={`note-delete-confirm-yes-${n.id}`}>{deleting ? "Deleting…" : "Confirm delete"}</button>
                            <button onClick={() => setConfirmDeleteId(null)}
                              className="label-mono text-muted hover:text-ink transition-colors"
                              data-testid={`note-delete-cancel-${n.id}`}>Cancel</button>
                          </span>
                        ) : (
                          <>
                            <button onClick={() => openEdit(n)}
                              className="label-mono border border-hairline px-2 py-0.5 hover:bg-ink hover:text-white transition-colors"
                              data-testid={`note-edit-${n.id}`}>Edit</button>
                            <button onClick={() => { setActionError(""); setConfirmDeleteId(n.id); }}
                              className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors"
                              data-testid={`note-delete-${n.id}`}>Delete</button>
                          </>
                        )}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-4 pt-1 text-sm text-ink whitespace-pre-wrap break-words" data-testid={`note-body-${n.id}`}>
                        {n.body}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
